/**
 * Reading files and folders dragged in from the OS (Finder, Explorer) — the
 * vault's drop-to-upload. The vault's own row drags (moves) never carry
 * `Files`, so the two kinds of drag cannot be confused.
 */

/** A dropped file and the folder path it sat in, relative to the drop. */
export interface DroppedFile {
  file: File;
  /** '' for a loose file, `'Folder/sub'` for one inside a dropped folder. */
  dir: string;
}

/** True for a drag that carries files from outside the page. */
export function isOsFileDrag(event: DragEvent): boolean {
  return event.dataTransfer?.types.includes('Files') ?? false;
}

function isHiddenSegment(segment: string): boolean {
  return segment.startsWith('.') && segment.length > 0;
}

/** Drops dotfiles and anything inside a dot-folder. */
export function filterHiddenFiles<T extends DroppedFile>(files: T[]): T[] {
  return files.filter(
    ({ file, dir }) =>
      !isHiddenSegment(file.name) && !dir.split('/').some(segment => isHiddenSegment(segment))
  );
}

/** The folder path of a file picked through a folder input (`webkitRelativePath`). */
export function relativeDir(file: File): string {
  const path = file.webkitRelativePath;
  const lastSlash = path.lastIndexOf('/');
  return lastSlash === -1 ? '' : path.slice(0, lastSlash);
}

function fileFromEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

/** `readEntries` hands back at most 100 entries per call; read until it runs dry. */
async function readAllEntries(directory: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = directory.createReader();
  const entries: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) return entries;
    entries.push(...batch);
  }
}

async function walkEntry(entry: FileSystemEntry, dir: string, into: DroppedFile[]): Promise<void> {
  if (entry.isFile) {
    into.push({ file: await fileFromEntry(entry as FileSystemFileEntry), dir });
    return;
  }
  if (entry.isDirectory) {
    const path = dir ? `${dir}/${entry.name}` : entry.name;
    for (const child of await readAllEntries(entry as FileSystemDirectoryEntry)) {
      await walkEntry(child, path, into);
    }
  }
}

/**
 * Every file in a drop, folders walked recursively, hidden files left out.
 *
 * The entries are taken synchronously: a DataTransfer's items are emptied once
 * the drop event's turn ends, so nothing may be awaited before this is called.
 */
export function readDroppedFiles(dataTransfer: DataTransfer): Promise<DroppedFile[]> {
  const entries = Array.from(dataTransfer.items)
    .filter(item => item.kind === 'file')
    .map(item => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => entry !== null);

  if (entries.length === 0) {
    const files = Array.from(dataTransfer.files).map(file => ({ file, dir: '' }));
    return Promise.resolve(filterHiddenFiles(files));
  }

  return (async (): Promise<DroppedFile[]> => {
    const files: DroppedFile[] = [];
    for (const entry of entries) await walkEntry(entry, '', files);
    return filterHiddenFiles(files);
  })();
}

/** "7 files in 2 folders" / "3 files" — how a drop is described. */
export function describeDrop(files: DroppedFile[]): string {
  const fileCount = files.length;
  const folderCount = new Set(files.filter(f => f.dir).map(f => f.dir.split('/')[0])).size;
  const filesLabel = `${fileCount} ${fileCount === 1 ? 'file' : 'files'}`;
  if (folderCount === 0) return filesLabel;
  return `${filesLabel} in ${folderCount} ${folderCount === 1 ? 'folder' : 'folders'}`;
}

/** Where a drop landed: a folder's or contract's name, or the table (the current folder). */
export type VaultFileDropTarget =
  { kind: 'directory' | 'contract'; uid: string; name: string } | { kind: 'here' };
